import { useEffect, useState } from 'react';
import { positionSelector } from '../store/reducers/positionSlice/positionSlice';
import { getWidthPosition } from '../helpers/getWidth';
import moment from 'moment/moment';
import { getUpdatePositions } from '../helpers/getCountDays';
import { useSelector } from 'react-redux';
import { useUpdateAll } from './useUpdateAll';
import { charterSelector } from '../store/reducers/charterSlice/charterSlice';
import { stageSelector } from '../store/reducers/stageSlice/stageSlice';
import { getChainConnection } from '../helpers/getChainConnection';
import { queueFetch } from '../helpers/queueFetch';

export const useResizePosition = (activeTab, position, isPlanFact) => {
    let width = 0;

    const daysCount = isPlanFact ? position?.actual?.days_count : position?.planned.days_count;

    width = getWidthPosition(Number(daysCount), activeTab);

    const allCharters = useSelector(charterSelector.selectAll);
    const allStage = useSelector(stageSelector.selectAll);
    const parentCharter = allCharters.find((charter) => charter.children.includes(position.guid));
    const parentStage = allStage.find((stage) => {
        const childGuid = parentCharter ? parentCharter.guid : position.guid;
        return stage.positions.includes(childGuid);
    });
    const { updateAll } = useUpdateAll(parentStage);

    const allPosition = useSelector(positionSelector.selectEntities);

    const [elWidth, setElWidth] = useState(width);
    const [needResizeUpdate, setNeedResizeUpdate] = useState(false);

    const changeWidth = (movementX) => {
        setElWidth((prevState) => {
            return prevState + movementX;
        });
    };

    const onItemChange = () => {
        setNeedResizeUpdate(true);
    };

    useEffect(() => {
        if (needResizeUpdate) {
            const queue = [];
            let days_count = Math.round(elWidth / 24);

            if (activeTab === 'week') {
                days_count = Math.round(elWidth / 11);
            }

            if (activeTab === 'month') {
                days_count = Math.round(elWidth / 7);
            }

            days_count = days_count < 1 ? 1 : days_count;

            if (+position.planned.days_count !== days_count) {
                let updatedPositionsEntities = { ...allPosition };

                const isFollowerTypeOne = position.connections.some(
                    (connection) => connection.type === 1 && connection.to === position.guid
                );

                if (isFollowerTypeOne) {
                    const connections = position.connections.filter(
                        (connection) => connection.type === 1 && connection.to === position.guid
                    );

                    const firstPredecessorStartDate = connections
                        .map((connection) => updatedPositionsEntities[connection.from])
                        .sort(
                            (positionA, positionB) =>
                                moment(positionA.planned.end_date).valueOf() -
                                moment(positionB.planned.end_date).valueOf()
                        )[0].planned.start_date;

                    if (
                        position.planned.end_date ===
                            moment(firstPredecessorStartDate).add(-1, 'days').format() &&
                        +position.planned.days_count < days_count
                    ) {
                        setElWidth(width);
                        setNeedResizeUpdate(false);
                        return;
                    } else {
                        const updatedEndDate = moment(position.planned.start_date)
                            .add(days_count - 1, 'days')
                            .format();

                        if (
                            moment(updatedEndDate).isAfter(
                                moment(firstPredecessorStartDate).add(-1, 'days')
                            )
                        ) {
                            const days_count = `${moment(firstPredecessorStartDate).diff(
                                moment(position.planned.start_date),
                                'days'
                            )}`;
                            const end_date = moment(firstPredecessorStartDate)
                                .add(-1, 'days')
                                .format();
                            const planned = {
                                ...position.planned,
                                days_count,
                                end_date,
                            };
                            updatedPositionsEntities[position.guid] = { ...position, planned };
                            setElWidth(width);
                            setNeedResizeUpdate(false);
                            updateAll(updatedPositionsEntities);

                            queue.push({
                                data: {
                                    data: {
                                        guid: position.guid,
                                        startDate: moment(planned.start_date).format('YYYY-MM-DD'),
                                        endDate: moment(planned.end_date).format('YYYY-MM-DD'),
                                    },
                                    type: 'update',
                                    action: 'resize_position',
                                },
                                subEntityType: 'step',
                                subEntityId: parentStage.id,
                                t: Date.now(),
                            });

                            queueFetch(queue);

                            return;
                        }
                    }
                }

                const nextPositions = position.connections
                    .filter(
                        (connection) => connection.type === 0 && position.guid === connection.from
                    )
                    .map((connection) => allPosition[connection.to]);

                const chainsConnections = nextPositions.map((position) => [
                    position,
                    ...getChainConnection(position, allPosition),
                ]);

                const moveDays = days_count - +position.planned.days_count;

                const startDate = position.planned.start_date;
                const endDate = moment(startDate)
                    .add(days_count - 1, 'days')
                    .format();

                const planned = {
                    days_count: `${days_count}`,
                    start_date: startDate,
                    end_date: endDate,
                };

                const editedItem = { ...position, planned };

                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(planned.start_date).format('YYYY-MM-DD'),
                            endDate: moment(planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'resize_position',
                    },
                    subEntityType: 'step',
                    subEntityId: parentStage.id,
                    t: Date.now(),
                });

                if (chainsConnections.length) {
                    chainsConnections.forEach((chain) => {
                        const nextPosition = chain[0];

                        const initEndDate = position.planned.end_date;

                        const nextPositionStartDate = nextPosition.planned.start_date;

                        const diffBeforePosition =
                            moment(nextPositionStartDate).diff(moment(initEndDate), 'days') - 1;

                        if (diffBeforePosition - moveDays < 0) {
                            const diff = Math.abs(diffBeforePosition - moveDays);

                            const updatePosition = getUpdatePositions(chain, diff);

                            updatePosition.forEach((position) => {
                                queue.push({
                                    data: {
                                        data: {
                                            guid: position.changes.guid,
                                            startDate: moment(
                                                position.changes.planned.start_date
                                            ).format('YYYY-MM-DD'),
                                            endDate: moment(
                                                position.changes.planned.end_date
                                            ).format('YYYY-MM-DD'),
                                        },
                                        type: 'update',
                                        action: 'resize_position',
                                    },
                                    subEntityType: 'step',
                                    subEntityId: parentStage.id,
                                    t: Date.now(),
                                });
                            });

                            updatedPositionsEntities[position.guid] = editedItem;

                            updatePosition.forEach((item) => {
                                updatedPositionsEntities[item.id] = item.changes;
                            });
                        }
                    });
                }

                queueFetch(queue);

                updateAll({ ...updatedPositionsEntities, [position.guid]: editedItem });
            }
            setElWidth(width);
            setNeedResizeUpdate(false);
        }
    }, [needResizeUpdate]);

    useEffect(() => {
        setElWidth(width);
    }, [position]);
    return {
        elWidth,
        needResizeUpdate,
        setNeedResizeUpdate,
        setElWidth,
        changeWidth,
        onItemChange,
    };
};
